import type {ThreeTiledTextureCallback} from "#three-texture-streaming/three-tiled-texture-callback"
import {Three as THREE} from "#three-texture-streaming/three-exports"
import type {TextureTileId} from "#three-texture-streaming/texture-tile-id"
import {TRACE_TILED_TEXTURES} from "#three-texture-streaming/texture-tile-constants"
import {TILE_REQUEST_BUFFER_SIZE_SCALE} from "#three-texture-streaming/three-viewport-texture-tile-updater-constants"
import type {ThreeViewportTextureTileUpdaterMaterialSet} from "#three-texture-streaming/three-viewport-texture-tile-updater-material-set"
import type {TileUpdaterMaterialInfo} from "#three-texture-streaming/tile-updater-material-info"
import {sceneLogger as logger} from "#three-texture-streaming/scene-logger"

const TRACE = TRACE_TILED_TEXTURES

type ProxyData = {
    originalMesh: THREE.Mesh
    proxies: THREE.Mesh[]
    materialSet: ThreeViewportTextureTileUpdaterMaterialSet
    materialInfo: TileUpdaterMaterialInfo
}

export class ThreeViewportTextureTileUpdater {
    constructor(
        private callback: ThreeTiledTextureCallback,
        private renderer: THREE.WebGPURenderer,
    ) {
        this._tileRequestBuffer = new THREE.RenderTarget(1, 1, {
            type: THREE.UnsignedByteType,
            format: THREE.RGBAFormat,
            generateMipmaps: false,
            minFilter: THREE.NearestFilter,
            magFilter: THREE.NearestFilter,
            colorSpace: THREE.NoColorSpace,
        })
    }

    dispose() {
        this.callback.getAtlasManager().unregisterViewport(this)
        this._tileRequestBuffer.dispose()
        this._clearAllProxies()
    }

    setSize(viewportWidth: number, viewportHeight: number): void {
        const devicePixelRatio = this.renderer.getPixelRatio()
        viewportWidth *= devicePixelRatio
        viewportHeight *= devicePixelRatio
        const width = Math.ceil(viewportWidth * TILE_REQUEST_BUFFER_SIZE_SCALE)
        const height = Math.ceil(viewportHeight * TILE_REQUEST_BUFFER_SIZE_SCALE)
        if (this._tileRequestBuffer.width === width && this._tileRequestBuffer.height === height) {
            return
        }
        this.callback.getAtlasManager().registerViewportSize(this, {width: viewportWidth, height: viewportHeight})
        if (this._readbackInProgress) {
            this._pendingSize = {width, height}
            return
        }
        this._tileRequestBuffer.setSize(width, height)
        if (TRACE) {
            logger.log(`ThreeViewportTextureTileUpdater: setSize: ${width}x${height}`)
        }
    }

    async update(scene: THREE.Scene, camera: THREE.Camera): Promise<void> {
        if (this._readbackInProgress) {
            return
        }

        // gather meshes and their referenced tiled textures
        const tiledMaterialInfosByThreeMesh = new Map<THREE.Mesh, TileUpdaterMaterialInfo>()
        let hasTiledTextures = false
        scene.traverseVisible((object) => {
            if (object instanceof THREE.Mesh) {
                const materialInfo = this.callback.getMeshMaterialInfo(object) ?? this._defaultMaterialInfo
                if (materialInfo.referencedTiledTextureInstances.length > 0) {
                    hasTiledTextures = true
                }
                if (materialInfo !== this._defaultMaterialInfo || hasTiledTextures) {
                    tiledMaterialInfosByThreeMesh.set(object, materialInfo)
                }
            }
        })

        // remove proxies for meshes that are no longer visible or tiled
        const proxyMeshesToRemove = Array.from(this._proxyByOriginalMesh.keys()).filter((mesh) => !tiledMaterialInfosByThreeMesh.has(mesh))
        proxyMeshesToRemove.forEach((mesh) => this._removeProxies(mesh))

        if (!hasTiledTextures) {
            return
        }

        this._frameIndex++

        // add/update proxies and sync world matrices
        for (const [mesh, materialInfo] of tiledMaterialInfosByThreeMesh) {
            const existing = this._proxyByOriginalMesh.get(mesh)
            if (!existing) {
                this._addProxies(mesh, materialInfo)
            } else {
                if (existing.materialInfo !== materialInfo) {
                    this._removeProxies(mesh)
                    this._addProxies(mesh, materialInfo)
                } else {
                    const activeIndex = this._frameIndex % existing.proxies.length
                    for (let i = 0; i < existing.proxies.length; i++) {
                        const proxy = existing.proxies[i]
                        proxy.visible = i === activeIndex
                        proxy.geometry = mesh.geometry
                        proxy.matrix.copy(mesh.matrixWorld)
                        proxy.matrixWorld.copy(mesh.matrixWorld)
                    }
                }
            }
        }

        // update frame index uniform on all active material sets
        for (const {materialSet} of this._proxyByOriginalMesh.values()) {
            materialSet.updateFrameIndex(this._frameIndex)
        }

        // render proxy scene to tile request buffer
        const prevRenderTarget = this.renderer.getRenderTarget()
        const prevClearColor = new THREE.Color()

        this.renderer.getClearColor(prevClearColor as any)
        const prevClearAlpha = this.renderer.getClearAlpha()
        this.renderer.setClearColor(0x000000, 0)
        this.renderer.setRenderTarget(this._tileRequestBuffer)
        this.renderer.clear()
        this.renderer.render(this._tileRequestScene, camera)
        this.renderer.setRenderTarget(prevRenderTarget)
        this.renderer.setClearColor(prevClearColor, prevClearAlpha)

        // read back tile request buffer
        this._readbackInProgress = this.renderer
            .readRenderTargetPixelsAsync(this._tileRequestBuffer, 0, 0, this._tileRequestBuffer.width, this._tileRequestBuffer.height)
            .then((pixelBuffer) => {
                if (this._pendingSize) {
                    const {width, height} = this._pendingSize
                    this._pendingSize = null
                    this._tileRequestBuffer.setSize(width, height)
                    if (TRACE) {
                        logger.log(`ThreeViewportTextureTileUpdater: setSize (deferred): ${width}x${height}`)
                    }
                }
                this.determinePageRequestsFromPixelBuffer(pixelBuffer)
            })
            .catch((error) => {
                // expected when the renderer is disposed (e.g. editor closed) while a readback is in flight
                if ((error as DOMException).name !== "AbortError") {
                    logger.error("ThreeViewportTextureTileUpdater: error reading back tile request buffer", error)
                }
            })
            .finally(() => {
                this._readbackInProgress = null
            })
    }

    private _addProxies(originalMesh: THREE.Mesh, materialInfo: TileUpdaterMaterialInfo): void {
        const materialSet = this.callback.getTileRequestShaderMaterials(materialInfo, this._frameIndex)
        const proxies = materialSet.materials.map((mat, i) => {
            const proxy = new THREE.Mesh(originalMesh.geometry, mat.material)
            proxy.visible = i === 0
            proxy.matrixAutoUpdate = false
            proxy.matrixWorldAutoUpdate = false
            proxy.matrix.copy(originalMesh.matrixWorld)
            proxy.matrixWorld.copy(originalMesh.matrixWorld)
            this._tileRequestScene.add(proxy)
            return proxy
        })
        this._proxyByOriginalMesh.set(originalMesh, {
            originalMesh,
            proxies,
            materialSet,
            materialInfo,
        })
    }

    private _removeProxies(originalMesh: THREE.Mesh): void {
        const proxyData = this._proxyByOriginalMesh.get(originalMesh)
        if (!proxyData) {
            throw new Error(`No proxy data found for mesh ${originalMesh.uuid}`)
        }
        for (const proxy of proxyData.proxies) {
            this._tileRequestScene.remove(proxy)
        }
        proxyData.materialSet.release()
        this._proxyByOriginalMesh.delete(originalMesh)
    }

    private _clearAllProxies(): void {
        Array.from(this._proxyByOriginalMesh.keys()).forEach((mesh) => this._removeProxies(mesh))
        this._proxyByOriginalMesh.clear()
    }

    private determinePageRequestsFromPixelBuffer(pixelBuffer: THREE.TypedArray): void {
        const requestedTileIds = new Set<TextureTileId>()
        for (let i = 0; i < pixelBuffer.length; i += 4) {
            const requestedTileId = pixelBuffer[i] | (pixelBuffer[i + 1] << 8) | (pixelBuffer[i + 2] << 16) | (pixelBuffer[i + 3] << 24)
            if (requestedTileId !== 0) {
                requestedTileIds.add(requestedTileId)
            }
        }
        if (requestedTileIds.size > 0) {
            this.callback.requestTiles(requestedTileIds)
        }
    }

    private _frameIndex = 0
    private _tileRequestBuffer: THREE.RenderTarget
    private readonly _tileRequestScene = new THREE.Scene()
    private _proxyByOriginalMesh = new Map<THREE.Mesh, ProxyData>()
    private _readbackInProgress: Promise<void> | null = null
    private _pendingSize: {width: number; height: number} | null = null

    private _defaultMaterialInfo: TileUpdaterMaterialInfo = {
        isTransparent: false,
        referencedTiledTextureInstances: [],
    }
}
